(ns ycweb.app.admin.stores.income
  "Store Income report screen (/admin/stores.htm?action=income).
   Renders the pivot table the PHP report produced, from the admin.stores.income endpoint."
  (:require
   [clojure.string :as str]
   [rum.core :as rum]
   [ycweb.app.admin.finance.common :as fincommon]
   [ycweb.app.dashboard.presence :as presence]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.datepicker :as datepicker]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.date :as ud]
   [ycweb.util.urlstate :as urlstate]))

(def screen :admin.stores/income)

(def columns-options
  [{:value "" :text "By Day/Week/Month"}
   {:value "rolling" :text "By 90/180/365 Days"}
   {:value "total" :text "Custom - Total Only"}
   {:value "months" :text "Custom - By Month"}
   {:value "weeks" :text "Custom - By Week"}
   {:value "days" :text "Custom - By Day"}
   {:value "dayofweek" :text "Custom - Day of Week"}
   {:value "hourofday" :text "Custom - Hour of Day"}
   {:value "dayandhour" :text "Custom - Day + Hour"}
   {:value "compareyoy" :text "Compare YoY"}])

(def other-reports
  [["assistants" "Assistants"] ["products" "Products"] ["payments" "Payments"] ["deliveries" "Deliveries"]])

;;;; Formatting

(defn ratio [a b]
  (if (and b (not (zero? b))) (/ (double (or a 0)) b) 0.0))

(defn money [n & [dp]] (str "R" (u/number-format (or n 0) (or dp 0))))
(defn int-str [n] (u/number-format (or n 0) 0))
(defn perc-str [n & [dp]] (str (u/number-format (* 100 (or n 0)) (or dp 1)) "%"))

(def row-specs
  "Metric rows in the order of storesincome.tpl. :value takes a metrics map.
   :better says which direction of a growth delta is good, for colour coding."
  [{:title "Income" :value :income :format #(money %) :compare :income}
   {:title "Sales" :value :sales :format int-str :link :saleslink :compare :sales}
   {:title "Items/Basket" :value #(ratio (:units %) (:sales %)) :format #(u/number-format % 1)
    :compare :itemssale :compare-abs? true}
   {:title "Income/Basket" :value #(ratio (:income %) (:sales %)) :format #(money %) :compare :incomebasket}
   :divider
   {:title "Stock Cost %" :value #(ratio (:stockcost %) (:income %)) :format perc-str
    :compare :stockcostperc :better :lower}
   {:title "Stock Cost" :value :stockcost :format #(money %)}
   {:title "Delivery %" :value #(ratio (:delivery %) (:income %)) :format perc-str
    :compare :deliveryperc :better :lower}
   {:title "Delivery" :value :delivery :format #(money %)}
   {:title "Transaction %" :value #(ratio (:transaction %) (:income %)) :format perc-str
    :compare :transactionperc :better :lower}
   {:title "Transaction" :value :transaction :format #(money %)}
   {:title "Packaging %" :value #(ratio (:packaging %) (:income %)) :format perc-str
    :compare :packagingperc :better :lower}
   {:title "Packaging" :value :packaging :format #(money %)}
   :divider
   {:title "GP %" :value #(ratio (:gp %) (:income %)) :format perc-str :compare :gpperc :better :higher}
   {:title "GP" :value :gp :format #(money %) :compare :gp}
   :divider
   {:title "Aftersales" :value :aftersales :format int-str :link :aftersaleslink}
   {:title "Stock Adjustments" :value :shrinkages :format #(money % 2) :link :shrinkageslink}
   {:title "Visitors" :value :visitors :format int-str :compare :visitors :hide-online? true :no-total? true}
   {:title "Sales/Visitors" :value #(ratio (:sales %) (:visitors %)) :format #(perc-str % 2)
    :hide-online? true :no-total? true}
   {:title "Income/Visitor" :value #(ratio (:income %) (:visitors %)) :format #(money % 2)
    :hide-online? true :no-total? true}])

(defn online? [store] (= "Online" (:name store)))

(defn growth-cell
  "Text and colour for a growth value, mirroring the PHP template's colour classes."
  [{:keys [compare-abs? better] compare-key :compare} compare-map]
  (when-let [v (and compare-key compare-map (get compare-map compare-key))]
    (let [text (if compare-abs? (u/number-format v 1) (perc-str v 1))
          colour
          (case better
            :lower (cond (neg? v) "green" (pos? v) "red" :else nil)
            :higher (cond (pos? v) "green" (neg? v) "red" :else nil)
            nil)]
      {:text text :colour colour})))

;;;; Report data helpers

(defn store-data [period store-id]
  (:data (first (filter #(= store-id (:store-id %)) (:rows period)))))

(defn store-total [report store-id]
  (first (filter #(= store-id (:store-id %)) (:store-totals report))))

(defn store-compare [report store-id]
  (first (filter #(= store-id (:store-id %)) (get-in report [:compare :stores]))))

(defn cell-text [{:keys [value] fmt :format} data]
  (if data (fmt (value data)) ""))

(defn report-lines
  "Every rendered line of the report as {:kind :header/:store/:total/:divider ...} with
   already formatted cells. Shared by the table and the CSV export."
  [{:keys [store stores periods showrowtotals showrowcompare all-totals] :as report}]
  (let [single? (some? store)]
    (->>
      row-specs
      (mapcat
        (fn [spec]
          (cond
            (= :divider spec)
            [{:kind :divider}]

            (and single? (:hide-online? spec) (online? store))
            []

            :else
            (let [store-lines
                  (for [s stores
                        :when (not (and (:hide-online? spec) (online? s)))]
                    {:kind :store
                     :label (if single? (:title spec) (:name s))
                     :cells (map (fn [p] {:text (cell-text spec (store-data p (:id s)))
                                          :link (get (store-data p (:id s)) (:link spec))})
                              periods)
                     :total (when showrowtotals (cell-text spec (store-total report (:id s))))
                     :growth (when showrowcompare (growth-cell spec (store-compare report (:id s))))})
                  total-line
                  (when (and (not single?) (not (:no-total? spec)))
                    {:kind :total
                     :label "Total:"
                     :cells (map (fn [p] {:text (cell-text spec (:totals p))}) periods)
                     :total (when showrowtotals (cell-text spec all-totals))
                     :growth (when showrowcompare (growth-cell spec (get-in report [:compare :all])))})]
              (concat
                (when-not single? [{:kind :header :label (:title spec)}])
                store-lines
                (when total-line [total-line])))))))))

(defn report->csv
  [{:keys [store periods showrowtotals showrowcompare] :as report}]
  (let [q (fn [s] (str "\"" (str/replace (str s) "\"" "\"\"") "\""))
        header
        (concat [(or (:name store) "")] (map :label periods)
          (when showrowtotals ["Totals"]) (when showrowcompare ["Growth"]))
        lines
        (for [{:keys [kind label cells total growth]} (report-lines report)
              :when (contains? #{:store :total :header} kind)]
          (if (= :header kind)
            [label]
            (concat [label] (map :text cells)
              (when showrowtotals [total]) (when showrowcompare [(:text growth)]))))]
    (str/join "\n" (map #(str/join "," (map q %)) (cons header lines)))))

;;;; Components

(defn td [k {:keys [text link]}]
  [:td.stats-table__td.u-text-right
   {:key k}
   (if (u/presence link) [:a {:href link} text] text)])

(defn growth-td [{:keys [text colour]}]
  [:td.stats-table__td.u-text-right
   {:key "growth"
    :style (when colour {:color colour :font-weight "bold"})}
   text])

(rum/defc income-table < rum/reactive
  [{:keys [store periods showrowtotals showrowcompare] :as report}]
  (let [colspan (+ (count periods) 1 (if showrowtotals 1 0) (if showrowcompare 1 0))]
    [:div.u-overflow-x-auto
     [:table.stats-table.u-margin-bottom-40
      [:thead
       [:tr.stats-table__row
        [:th.stats-table__th (or (:name store) "")]
        (for [{:keys [label]} periods] [:th.stats-table__th.u-text-right {:key label} label])
        (when showrowtotals [:th.stats-table__th.u-text-right "Totals"])
        (when showrowcompare [:th.stats-table__th.u-text-right "Growth"])]]
      [:tbody
       (map-indexed
         (fn [i {:keys [kind label cells total growth]}]
           (case kind
             :divider [:tr.stats-table__row {:key i} [:td {:col-span colspan} [:hr]]]
             :header [:tr.stats-table__row {:key i} [:th.stats-table__th {:col-span colspan} label]]
             [:tr.stats-table__row
              {:key i
               :style (when (= :total kind) {:font-weight "bold"})}
              [:th.stats-table__th label]
              (map-indexed (fn [j c] (td j c)) cells)
              (when showrowtotals (td "total" {:text total}))
              (when showrowcompare (growth-td growth))]))
         (report-lines report))]]]))

(rum/defc filter-form < rum/reactive [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        filter-atom (rum/cursor-in process-atom [:income :filter])
        report (cmn/curact process-atom [:income :report])
        columns (rum/react (rum/cursor-in filter-atom [:columns]))
        show-dates? (not (contains? #{"" "rolling" nil} columns))
        store-options
        (map
          (fn [s] {:value (if (:id s) (str (:id s)) "") :text (:name s)})
          (:allstores report))]
    [:form.form.u-margin-bottom-20
     {:on-submit (fn [ev] #?(:cljs (.preventDefault ev) :clj ev) (grid/dispatch-action app-atom :admin.stores/income-search {}))}
     [:div {:class (u/bem :flex-grid-group [:flush-force :align-left :valign-bottom])}
      [:div {:class (u/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (forms/select "Store:" (rum/cursor-in filter-atom [:id]) store-options {})]]
      [:div {:class (u/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (forms/select "Columns:" (rum/cursor-in filter-atom [:columns]) columns-options {})]]
      (when show-dates?
        (list
          [:div {:key "from" :class (u/bem :flex-grid-block [:fluid])}
           [:div.flex-grid-content
            (datepicker/datepicker app-atom "Date From:" "dd-mm-yyyy"
              [:admin.stores :income :filter :datefrom]
              {:header "Date From" :datatype :datedmy})
            (forms/input "or days ago:" "" (rum/cursor-in filter-atom [:datefrom-days])
              {:type :text :small-width true})]]
          [:div {:key "to" :class (u/bem :flex-grid-block [:fluid])}
           [:div.flex-grid-content
            (datepicker/datepicker app-atom "Date To:" "dd-mm-yyyy"
              [:admin.stores :income :filter :dateto]
              {:header "Date To" :datatype :datedmy})
            (forms/input "or days ago:" "" (rum/cursor-in filter-atom [:dateto-days])
              {:type :text :small-width true})]]))
      [:div {:class (u/bem :flex-grid-block [:fluid]) :style {:margin-left "auto"}}
       [:div.flex-grid-content
        [:div.form-field
         [:label.form-label {:style {:visibility "hidden"}} "Actions"]
         [:div {:style {:display "flex" :gap "8px" :align-items "center"}}
          (buttons/button-web "Filter"
            {:on-click #(grid/dispatch-action app-atom :admin.stores/income-search {})
             :type :button
             :style :primary
             :size :default})
          (when report
            (buttons/button-web "Export to CSV"
              {:on-click #(grid/dispatch-action app-atom :admin.stores/income-csv {})
               :type :button
               :style :secondary
               :size :default}))]]]]]]))

(rum/defc income-screen < rum/reactive [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        report (cmn/curact process-atom [:income :report])
        store-id (cmn/curact process-atom [:income :filter :id])]
    [:<>
     [:div.admin-react-wrapper__content
      (headings/heading (str "Store Income - " (or (get-in report [:store :name]) "All")) {:type :h1 :size :large})
      (when-let [asof (:asof report)]
        [:p.u-margin-bottom-20
         (str "Figures last refreshed " asof ". Today's figures update every "
           (:refresh-minutes report) " minutes, so a sale rung up after that time appears at the next refresh.")])]
     [:div.u-margin-bottom-20
      [:a (urlstate/href-props app-atom "stores") "Back to Stores List"]
      " | Report by: "
      (interpose " · "
        (for [[action title] other-reports]
          [:a (assoc (urlstate/href-props app-atom "stores" action
                       (u/maybe assoc {} :id (u/presence store-id)))
                :key action)
           title]))]
     (filter-form app-atom)
     (when report
       (income-table report))
     (toaster/loading-overlay app-atom screen)]))

;;;; Transitions and comms

(defn filter->params [{:keys [id columns datefrom dateto datefrom-days dateto-days]}]
  (u/maybe assoc
    {:columns (or columns "")}
    :id (u/presence id)
    :datefrom (when datefrom (ud/php-timestamp datefrom))
    :dateto (when dateto (ud/php-timestamp dateto))
    :datefrom-days (u/presence datefrom-days)
    :dateto-days (u/presence dateto-days)))

(defn send-report [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)]
    (toaster/open-overlay app-atom screen)
    (comms/send screen (filter->params (get-in @process-atom [:income :filter])))))

(defmethod grid/action :admin.stores/income-search [app-atom _ _]
  (send-report app-atom))

(defmethod grid/action :admin.stores/income-csv [app-atom _ _]
  (let [process-atom (cmn/process-cursor app-atom)]
    (fincommon/download-as-csv (report->csv (get-in @process-atom [:income :report])) "store-income")))

(defmethod comms/receive screen [app-atom {:keys [success] :as report}]
  (toaster/close-overlay app-atom screen)
  (if success
    (swap! (cmn/process-cursor app-atom) assoc-in [:income :report] report)
    (toaster/open-toast app-atom :error "Could not load the store income report" {})))

(defmethod urlstate/screen-params-shape screen [_]
  {:datefrom :integer
   :dateto :integer
   :datefrom_days :integer
   :dateto_days :integer})

(defmethod t/transition-open screen
  [app-atom _ {:keys [id columns datefrom dateto datefrom_days dateto_days]}]
  (swap! (cmn/process-cursor app-atom) assoc-in [:income :filter]
    {:id (if id (str id) "")
     :columns (or columns "")
     :datefrom (when datefrom (ud/from-php-date datefrom))
     :dateto (when dateto (ud/from-php-date dateto))
     :datefrom-days (some-> datefrom_days str)
     :dateto-days (some-> dateto_days str)})
  (send-report app-atom))

(defmethod t/transition-after-open screen [app-atom _ _]
  (presence/open app-atom {:context/ident :admin.stores}))

(defmethod flow/render-screen screen [app-atom _]
  (cmn/permission-lock app-atom "SiteAdmin" (income-screen app-atom)))
